/**
 * Content for the "How It Works" page — plain-English flow diagrams that explain
 * how parts of the system behave, so the team can understand the logic without
 * reading code. Each entry is a Mermaid diagram + a short summary, grouped into
 * sections (rendered Guides-style: a topic sidebar + the selected diagram).
 *
 * To add a new flow: append a `SystemFlow` with a unique `key`. Keep labels in
 * PLAIN ENGLISH (no code identifiers) — these are read by non-technical team
 * members. Verify any new Mermaid renders before committing.
 */

export interface SystemFlow {
  /** Section heading the topic is grouped under in the sidebar. */
  section: string;
  /** Unique, URL-safe id used as the active-topic key. */
  key: string;
  /** Sidebar + heading title. */
  title: string;
  /** One-or-two sentence plain-English summary shown above the diagram. */
  summary: string;
  /** Mermaid source (flowchart / sequence / etc.). */
  mermaid: string;
}

/**
 * Shared product colour key appended to EVERY diagram, so the experience is
 * consistent: the legend is always visible. Nodes that are genuinely
 * product-specific get coloured (blue = Broker AI, purple = Intelligent
 * Automation, green = both products) via a per-diagram `class` line. A
 * product-agnostic diagram assigns NO product classes — its nodes keep the
 * default style (a standard look) while the key still sits below for reference.
 *
 * Note: `classDef both` is green; status colours used elsewhere must not reuse
 * that green (a Broker-only success node is coloured blue, not green, so it
 * doesn't read as "both"). Red (#ffcdd2) for a bad/suspended state is fine —
 * it doesn't collide with any product colour.
 */
const PRODUCT_LEGEND = `
    classDef broker fill:#bbdefb,stroke:#1565c0,color:#0d47a1
    classDef iam fill:#e1bee7,stroke:#6a1b9a,color:#4a148c
    classDef both fill:#c8e6c9,stroke:#2e7d32,color:#1b5e20
    subgraph Legend["Legend — which product each path is for"]
      direction LR
      LB["Broker AI"]:::broker
      LI["Intelligent Automation"]:::iam
      LM["Both products"]:::both
    end`;

export const SYSTEM_FLOWS: SystemFlow[] = [
  {
    section: 'Usage & Limits',
    key: 'runs',
    title: 'Runs — how a monthly run limit is decided',
    summary:
      "When someone runs a tool, the system decides WHICH monthly allowance applies by walking these checks in order — the first one that matches wins. That step is the same for both products. What differs is the UNIT the allowance is counted in: Broker AI counts CREDITS (a run costs 0.75–2 credits — see the Credits flow), Intelligent Automation counts whole runs. This is the 'Credits X/20' (Broker AI) or 'Monthly usage X/120' (Intelligent Automation) number on the dashboard.",
    mermaid: `flowchart TD
    A["Someone runs a tool.<br/>How many runs do they get this month?"] --> B{"Did the ORG buy one shared<br/>package for everyone, and is it paid up?"}
    B -- Yes --> R0["Everyone shares the ORG's package.<br/>Counts against the WHOLE ORG's tally."]

    B -- No --> C{"Is this ORG a shared pool?<br/>(everyone draws from one org allowance)"}

    C -- "Yes (shared pool)" --> R2["Use the ORG's monthly allowance.<br/>Counts against the WHOLE ORG.<br/>A personal package is ignored here."]

    C -- "No (not a pool)" --> D{"Does THIS person have their own<br/>personal package bought just for them?"}
    D -- Yes --> R1["Use their personal allowance.<br/>Counts against JUST THEM."]

    D -- No --> E{"Does this person have a<br/>special limit set on their login?"}
    E -- Yes --> R3["Use that special number.<br/>Counts against JUST THEM."]
    E -- No --> R4["Use the default<br/>(50 runs / 20 doc-chat / 20 research)."]

    R0 --> F["Count usage this month against whichever<br/>number won — in CREDITS for Broker AI<br/>(a run costs 0.75–2, see Credits flow),<br/>in whole runs for Intelligent Automation.<br/>Block them when they're over."]
    R2 --> F
    R1 --> F
    R3 --> F
    R4 --> F${PRODUCT_LEGEND}
    style R2 fill:#ffe0b2,stroke:#e65100`,
  },
  {
    section: 'Usage & Limits',
    key: 'usage-buckets',
    title: 'Usage buckets — the three separate counters',
    summary:
      'Each organisation tracks three independent monthly counters. Running a tool, using Doc Chat, and using Legal Research each draw from their own bucket — they do not share a limit. Same for both products, so nothing here is colour-coded.',
    mermaid: `flowchart TD
    U["What is the user doing?"] --> A{"Which action?"}
    A -- "Runs a tool (ROA, quote comparison, etc.)" --> B["Main allowance — CREDITS for Broker AI<br/>(e.g. 12/20) or general runs for<br/>Intelligent Automation (e.g. 100/120)"]
    A -- "Asks a question about a document" --> C["Doc Chat — e.g. 2/120"]
    A -- "Does legal research" --> D["Legal Research — e.g. 0/120"]
    B --> E["Each bucket has its own limit and its own tally.<br/>Only the main allowance (credits / general runs)<br/>has the shared-pool / personal-package logic<br/>(see the Runs flow)."]
    C --> E
    D --> E${PRODUCT_LEGEND}`,
  },
  {
    section: 'Usage & Limits',
    key: 'credits',
    title: 'Credits — what a single run actually costs (Broker AI)',
    summary:
      "Broker AI only. The monthly allowance is measured in CREDITS, and a single run costs between 0.75 and 2 credits depending on how big the job is — never more than 2 (Marcus's cap). A typical job costs about 1 credit. So a Bronze org (20 credits) gets roughly 20 normal runs a month, fewer if its runs are large. (Intelligent Automation is billed per person by the account manager and does not use credits.)",
    mermaid: `flowchart TD
    A["A broker runs a tool.<br/>How many credits does it cost?"] --> B["Start from the tool's base cost<br/>(most tools = 1 credit)"]
    B --> C{"How big was the job?<br/>(total words read in + written out)"}
    C -- "Small (under ~60k)" --> M1["× 0.75<br/>(a tiny job still costs a little)"]
    C -- "Normal (~60k–200k)" --> M2["× 1<br/>(a normal job ≈ 1 credit)"]
    C -- "Large (~200k–350k)" --> M3["× 1.5"]
    C -- "Very large (over ~350k)" --> M4["grows with size"]
    M1 --> CAP["Final cost — but NEVER more than<br/>2 credits per run (the cap)"]
    M2 --> CAP
    M3 --> CAP
    M4 --> CAP
    CAP --> D["Subtract from the org's monthly credits<br/>(Bronze 20 · Silver 50 · Gold 100)"]
    D --> G{"Any credits left?"}
    G -- "Yes" --> H["The run goes ahead."]
    G -- "No" --> I["Blocked until next month, or until an<br/>admin tops up / resets the org's pool."]${PRODUCT_LEGEND}
    class A,H broker
    style I fill:#ffcdd2,stroke:#c62828`,
  },

  // ── Billing & Payments — how an org is billed, the three ways someone pays,
  //    comp'd plans, and a subscription's lifecycle. Plain English only.
  {
    section: 'Billing & Payments',
    key: 'billing-overview',
    title: 'How an organisation gets billed',
    summary:
      'Every organisation is billed in one of these ways. The colours show which product each path applies to — Broker AI, Intelligent Automation, or both (see the legend). The diagrams after this one zoom into each path.',
    mermaid: `flowchart TD
    A["An organisation needs a plan.<br/>How does it get billed?"] --> B{"Are we sponsoring this org?<br/>(a comp'd / 'managed' plan)"}
    B -- "Yes — we pay" --> R0["MANAGED. We pay the bill. The org runs at<br/>its chosen tier for free, never sees a<br/>payment page, no card needed."]
    B -- "No — the org pays" --> C{"Which product is this org on?"}
    C -- "Broker AI" --> R1["Shared plan: ONE flat tier price<br/>(Bronze R1600 / Silver R3500 / Gold R6000).<br/>Everyone shares one pool of monthly runs."]
    C -- "Intelligent Automation" --> R2["Per person: each member has their OWN tier<br/>and price. The monthly bill is the sum,<br/>arranged with the account manager."]
    R1 --> D{"Was the org referred by FSP Hub?"}
    D -- "Yes" --> R3["Take 25% off the price<br/>(every month, not just the first charge)."]
    D -- "No" --> R4["Charge the full price."]
    R3 --> E["The card is entered by the customer<br/>(self-checkout) or by the org owner<br/>(an admin sends them a link) — or the broker<br/>pays by bank transfer (EFT), see the EFT flow."]
    R4 --> E
    R2 --> P["No payment page or self-checkout — the<br/>account manager sets it up (sales-led)."]${PRODUCT_LEGEND}
    class R0 both
    class R1,R3,R4,E broker
    class R2,P iam`,
  },
  {
    section: 'Billing & Payments',
    key: 'billing-status-tags',
    title: 'What the Trial / Unpaid / Paid / Managed labels mean',
    summary:
      "Broker AI only. Each org in the Organisations list carries one billing label. This is how it's chosen: MANAGED = we sponsor them (a comp'd plan); otherwise TRIAL = on the free 14-day trial with no card charged yet, PAID = an active paying subscription, UNPAID = no live subscription (never paid, the trial has expired, or it was cancelled / suspended). When a trial ends, the org flips to PAID if they entered a card, or UNPAID if they didn't. (Intelligent Automation orgs are billed manually by the account manager, so they don't carry these labels.)",
    mermaid: `flowchart TD
    A["Each org in the Organisations list shows<br/>one billing label. How is it picked?"] --> B{"Are we sponsoring this org?<br/>(a comp'd / 'managed' plan — we pay)"}
    B -- "Yes — we pay" --> R0["MANAGED<br/>We pay the bill. No card, and they never<br/>see a payment page."]
    B -- "No — the org pays itself" --> C{"Do they have a live, working<br/>subscription right now?"}
    C -- "No live subscription" --> R1["UNPAID<br/>Never paid, the free trial has expired,<br/>or it was cancelled / suspended."]
    C -- "Yes" --> D{"Are they still inside their<br/>free 14-day trial?"}
    D -- "Yes — no card charged yet" --> R2["TRIAL<br/>Signed up and trying it free.<br/>No payment taken yet."]
    D -- "No — a card is being charged" --> R3["PAID<br/>An active, paying subscription."]
    R2 --> E["When the 14-day trial ends it flips to<br/>PAID (if they entered a card) or<br/>UNPAID (if they didn't)."]
    class A,R3 broker${PRODUCT_LEGEND}
    style R0 fill:#eceff1,stroke:#546e7a
    style R2 fill:#fff3e0,stroke:#e65100
    style R1 fill:#ffcdd2,stroke:#c62828`,
  },
  {
    section: 'Billing & Payments',
    key: 'self-checkout',
    title: 'Customer self-checkout (they pay themselves)',
    summary:
      'Broker AI only. A broker on the app picks a plan and pays for it themselves. On broker production today this can run through an interim by-hand path instead. (Intelligent Automation orgs are set up by their account manager, not self-checkout.)',
    mermaid: `flowchart TD
    A["A broker picks a plan from the<br/>pricing cards in the app."] --> M{"Is the interim by-hand<br/>sign-up mode switched on?"}
    M -- "Yes (broker production today)" --> MJ["The button opens a short form.<br/>Our team creates the org and emails<br/>them a payment link to pay."]
    M -- "No (the normal flow)" --> B["They land on the Checkout page,<br/>see the plan and price (with the 25% FSP<br/>discount if it applies) and enter their name."]
    B --> C["They're sent to the secure card page<br/>(hosted by our payment provider) and<br/>enter their bank card."]
    C --> D["They come back to a 'Payment confirmed'<br/>screen — the subscription is now active."]
    MJ --> D
    D --> E["From now on they manage everything on the<br/>Manage Subscription page: replace card, view<br/>invoices, set billing details, invite teammates<br/>for free, or cancel."]${PRODUCT_LEGEND}
    class A,D,E broker`,
  },
  {
    section: 'Billing & Payments',
    key: 'payment-link',
    title: 'Admin payment link (the owner pays)',
    summary:
      "Broker AI only. For a self-serve org, an admin can nominate one person as the 'owner' and generate a payment link for them to pay — without that person needing to log in.",
    mermaid: `flowchart TD
    A["An internal admin opens the org<br/>in the admin dashboard."] --> B["Step 1: pick one of the org's people<br/>as the 'owner' and save it."]
    B --> C["Step 2: choose a plan<br/>(Bronze / Silver / Gold) and click<br/>'Generate payment link'."]
    C --> D["A secure payment link is created, billing<br/>the owner. FSP-Hub orgs get the 25%<br/>discount automatically."]
    D --> E["The admin copies the link and sends<br/>it to the owner."]
    E --> F["The owner opens it and enters their<br/>card — WITHOUT logging in."]
    F --> G["The subscription goes active and charges<br/>that card every month. Same as a<br/>self-checkout from here on."]
    N["Only for self-serve orgs.<br/>A comp'd (managed) org needs no link."] -.-> A${PRODUCT_LEGEND}
    class A,G broker`,
  },
  {
    section: 'Billing & Payments',
    key: 'eft-payment',
    title: 'Bank transfer / EFT (they pay by invoice)',
    summary:
      "Broker AI only. A fourth way a broker can pay — by bank transfer (EFT) instead of a card. We email them a pro-forma invoice with our banking details and a unique payment reference; they pay from their own bank and email back their proof of payment. The system reads that proof automatically to speed things up, but a person ALWAYS confirms the money actually landed before the plan switches on — a proof screenshot on its own never activates anything (it can be faked). Because EFT isn't an auto-debit like a card, a fresh invoice goes out every month, and a month that stays unpaid eventually pauses the org to read-only until it's settled. Two separate things can mark a month as paid: the customer emails proof to our processing inbox, OR anyone on our side ticks it off in the EFT Payments queue (used when they paid but sent the proof straight to their account manager instead). An org is only ever paused when NEITHER of those happened — and the moment either one lands, access comes straight back. (Intelligent Automation orgs are billed by their account manager, so this doesn't apply there.)",
    mermaid: `flowchart TD
    A["A broker chooses to pay by bank transfer (EFT)<br/>instead of entering a card at checkout."] --> B["The system emails the broker AND our account<br/>manager a pro-forma invoice — the amount, our<br/>banking details, and a unique payment reference.<br/>FSP-Hub orgs get the 25% discount on it."]
    B --> C["The broker makes the bank transfer from their<br/>own bank, quoting that reference."]
    C --> D["They email their proof of payment back to us<br/>(a screenshot or a PDF)."]
    D --> E["The system reads the proof automatically — it<br/>matches the reference and the amount, then flags<br/>the payment for our account manager to review.<br/>It NEVER switches the plan on by itself<br/>(a proof screenshot can be faked)."]
    E --> F["Our account manager opens the EFT Payments<br/>queue in the admin dashboard and checks the<br/>money actually landed in our bank account."]
    F --> G{"Has the money really cleared?"}
    G -- "Yes" --> H["The manager clicks 'Confirm &amp; Activate'<br/>and the subscription goes live."]
    G -- "A bank feed can confirm an exact match instead" --> H
    G -- "No / wrong amount" --> I["Left waiting — the manager follows up with the<br/>broker, or the request lapses if it's never paid."]
    H --> J["From now on the org runs on its plan, exactly<br/>like any other paying subscription."]
    J --> K["EFT is not an auto-debit, so every month the<br/>system emails a FRESH invoice for the new month<br/>(to the broker AND the account manager). This can<br/>go out on a set day — e.g. the 25th — or on the<br/>plan's own anniversary date."]
    K --> L{"Has that month been marked as paid?<br/>EITHER of two things counts."}
    L -- "They emailed proof to our<br/>processing inbox" --> P["The system reads the proof, matches it, and puts<br/>it in the EFT Payments queue for a person to<br/>confirm — same check as the very first payment."]
    L -- "They paid but sent proof<br/>straight to their account manager" --> Q["Anyone on our side opens the EFT Payments queue,<br/>finds that org's month, and clicks<br/>'Confirm &amp; Activate'. No proof needs to be in<br/>the system — this is the manual tick-off."]
    P --> R["That month is settled."]
    Q --> R
    R --> N["Nothing more to do until next month,<br/>when a new invoice goes out again."]
    N --> K
    L -- "Neither — still unpaid" --> M["A reminder is emailed, and the account manager<br/>is flagged that the account is overdue."]
    M --> S{"Still unpaid once the grace period<br/>runs out — e.g. by the 1st?"}
    S -- "No, it got paid in time" --> R
    S -- "Yes, still nothing" --> T["The org is PAUSED to read-only: they can still<br/>see their work but can't run anything new.<br/>They and their account manager are emailed."]
    T --> U["The moment the payment is confirmed — either<br/>way above — full access comes straight back<br/>automatically. Nothing to switch on by hand."]
    U --> K${PRODUCT_LEGEND}
    class A,H,J,K,N,R,U broker
    style E fill:#fff3e0,stroke:#e65100
    style I fill:#ffcdd2,stroke:#c62828
    style M fill:#fff3e0,stroke:#e65100
    style T fill:#ffcdd2,stroke:#c62828`,
  },
  {
    section: 'Billing & Payments',
    key: 'managed-plan',
    title: 'Managed / comp’d plan (we pay)',
    summary:
      'Works for either product (Broker AI or Intelligent Automation). When we sponsor an org, an admin flips it to a managed plan. It runs for free at a chosen tier and never sees a payment page. The higher Gold tiers exist for big sponsored orgs.',
    mermaid: `flowchart TD
    A["We've agreed to sponsor an org<br/>(e.g. a partner or a special deal)."] --> B["An admin opens the org and clicks<br/>'Activate managed plan' at a chosen tier."]
    B --> T{"How many runs a month do they need?"}
    T -- "Up to 100" --> T1["Bronze (20) / Silver (50) / Gold (100)"]
    T -- "More than 100" --> T2["Gold 2X (200) / Gold 3X (300) / Gold 4X (400)<br/>— comp'd-only tiers for big sponsored orgs."]
    T1 --> R["The org is now MANAGED: it runs at that<br/>tier for FREE, with no card and NO payment<br/>page anywhere in their app ('we pay')."]
    T2 --> R
    R --> S["Later, an admin can click 'Switch to self-serve'<br/>to remove the comp — then the org starts<br/>paying and sees the payment page again."]${PRODUCT_LEGEND}
    class R both`,
  },
  {
    section: 'Billing & Payments',
    key: 'subscription-lifecycle',
    title: "A subscription's life (trials, renewals, failed payments, cancelling)",
    summary:
      'Broker AI billing. What happens to a paying subscription over time — from the free trial, through automatic monthly charges, what happens if a payment fails, and cancelling. (Intelligent Automation is billed manually by the account manager, so this lifecycle does not apply there.)',
    mermaid: `flowchart TD
    A["Free trial<br/>(14 days, 10 runs, no card)"] --> B["They choose a plan and pay<br/>(self-checkout or an owner link)."]
    B --> C["ACTIVE — paid and working. The card is<br/>charged automatically every month on<br/>their sign-up date."]
    C --> D{"Does the monthly charge go through?"}
    D -- "Yes" --> C
    D -- "No (payment fails)" --> E["GRACE: they keep working, but we<br/>email them to update their card."]
    E --> F{"Sorted out within 3 tries?"}
    F -- "Yes" --> G["Recovered — back to normal,<br/>we email a confirmation."]
    G --> C
    F -- "No (3 failures)" --> H["SUSPENDED: the org goes read-only<br/>until they update their card."]
    H --> G
    C --> I["If they cancel: billing stops, but they keep<br/>access until the paid month ends.<br/>Re-subscribing means a fresh checkout."]${PRODUCT_LEGEND}
    class A,C broker
    style H fill:#ffcdd2,stroke:#c62828`,
  },
  {
    section: 'Billing & Payments',
    key: 'card-and-billing-details',
    title: 'Who can change the card, and what an invoice looks like',
    summary:
      "Broker AI card customers. Two things people ask about. FIRST, who may change the card on file: the person whose card it is (the payer) OR the organisation owner — so a business isn't stuck when the payer is on leave or has left. Anyone else sees the card status and a line telling them the owner can change it, rather than a dead end. If the owner puts a NEW card on, the payer moves to them, but only once the bank actually confirms the new card — abandoning the payment page halfway changes nothing. SECOND, invoices: a card customer's invoice is a RECEIPT, not a bill. The money was already taken, so it reads 'Paid in full' and shows no bank details — sending our account number for money we've already collected is how someone pays twice. Invoices carry the organisation's name; a customer can add their registered name, address and VAT number under Manage Subscription → Billing details and every invoice from then on carries them. EFT-invoiced and managed accounts are unaffected — their invoices are genuine requests for payment, and their billing stays with their account manager.",
    mermaid: `flowchart TD
    A["Someone on the Manage Subscription page<br/>wants to change the card."] --> B{"Are they the payer,<br/>or the organisation owner?"}
    B -- "Neither" --> C["They see the card status and<br/>'your organisation owner can change<br/>the card on file'. No dead end."]
    B -- "Yes" --> D["They go to the secure card page<br/>and enter the new card."]
    D --> E{"Did the bank confirm it?"}
    E -- "No (they abandoned it)" --> F["Nothing changes — the OLD card is<br/>still the live one, and its owner is<br/>still the payer."]
    E -- "Yes" --> G["New card is live. If the OWNER put it on,<br/>the payer moves to them now."]
    G --> H["Next month's charge goes through<br/>automatically, as before."]
    H --> I["An invoice is produced straight after the<br/>charge — a RECEIPT: 'Paid in full',<br/>no bank details, nothing owing."]
    I --> J["They download it from Invoice history.<br/>Adding billing details puts their registered<br/>name, address and VAT number on future ones."]${PRODUCT_LEGEND}
    class A,C,D,J broker
    style F fill:#fff3e0,stroke:#ef6c00`,
  },
  {
    section: 'Billing & Payments',
    key: 'plan-change',
    title: 'Changing plan (upgrade or downgrade)',
    summary:
      "Broker AI only. How a broker moves between Bronze / Silver / Gold on the Manage Subscription page. Only the org owner can do it, and an UPGRADE and a DOWNGRADE work very differently — because our payment provider's subscriptions can't be edited once created. (The new upgrade re-authorise path ships behind a switch: on in staging, off in production until it's signed off. Downgrades are unchanged.)",
    mermaid: `flowchart TD
    A["The org owner opens Manage Subscription,<br/>clicks 'Change plan' and picks a tier."] --> B{"A dearer tier (upgrade)<br/>or a cheaper one (downgrade)?"}
    B -- "Downgrade" --> D1["Nothing changes today. They keep their<br/>current plan and run limit until the month<br/>ends, then drop to the lower tier and price<br/>at the next billing date."]
    B -- "Upgrade" --> U1["They enter their name and are sent to the<br/>secure card page to re-confirm their card."]
    U1 --> U2["Behind the scenes a NEW higher-tier<br/>subscription is created (the old one can't be<br/>edited). Once the card is confirmed, the OLD<br/>subscription is cancelled automatically — so<br/>they are never charged on two plans at once."]
    U2 --> U3["Their first charge on the new plan is the full<br/>new price PLUS a one-time pro-rata top-up for<br/>the rest of the current month; the normal<br/>price every month after that."]
    U3 --> U4["The bigger plan's higher run limit takes<br/>effect as soon as the card is confirmed.<br/>If they abandon the card page, nothing<br/>changes — the old plan just carries on."]${PRODUCT_LEGEND}
    class A,D1,U1,U2,U3,U4 broker`,
  },
  {
    section: 'Billing & Payments',
    key: 'billing-date',
    title: 'Which day of the month you are charged',
    summary:
      "Broker AI only. Which day a subscription is billed and when the monthly run allowance resets. Today each org is billed on its own SIGN-UP date (its 'anniversary'), so different orgs reset on different days. A switch (on in staging, off in production) moves everyone to the 1ST of the month instead — with a smaller pro-rata first charge for the part of the month already used — so allowances reset for everyone on the same day.",
    mermaid: `flowchart TD
    A["A broker signs up and pays."] --> M{"Is the 'bill on the 1st' switch on?"}
    M -- "Off (today)" --> O1["Charged the FULL month straight away, then<br/>the same amount every month on their<br/>SIGN-UP date (e.g. always the 15th)."]
    O1 --> O2["Their monthly run allowance resets on that<br/>sign-up date — so different orgs reset on<br/>different days of the month."]
    M -- "On" --> N1["Charged a SMALLER pro-rata amount now —<br/>only for the days left until the 1st."]
    N1 --> N2["Then the full price on the 1st of every month,<br/>and their allowance resets on the 1st —<br/>the SAME day for everyone."]
    N1 --> N3["If they sign up in the last day or two before<br/>the 1st, they simply pay a full month now and<br/>start on the following 1st — never a tiny charge."]${PRODUCT_LEGEND}
    class A,O1,O2,N1,N2,N3 broker`,
  },
  {
    section: 'Billing & Payments',
    key: 'bulk-invoices',
    title: 'Invoicing many organisations at once',
    summary:
      "Works for either product. Invoices are normally created one organisation at a time, from that org's own page. The Bulk Invoices page does the whole book in one go: pick a range of months, pick the organisations (or all of them), and either PREVIEW what each would be invoiced, GENERATE the invoices and download them as a single zip, or EXPORT invoices that already exist. Preview creates nothing at all — worth running first on past months, because a back-dated invoice is built from the organisation's billing details and seats AS THEY ARE TODAY, not as they were back then. An organisation missing its billing name or address is simply skipped and listed, so one incomplete org never stops the rest. Re-running a month that was already invoiced reuses the SAME invoice number — it never issues a second number for the same month.",
    mermaid: `flowchart TD
    A["Someone on our side opens Bulk Invoices<br/>in the admin dashboard."] --> B["They pick a month range<br/>(e.g. March through July)."]
    B --> C["They tick specific organisations,<br/>or 'All organisations'."]
    C --> D{"Which of the three do they run?"}
    D -- "Preview" --> P1["The system works out what EACH organisation<br/>would be invoiced for each month — and creates<br/>NOTHING. No invoice number is used up and no<br/>invoice is produced."]
    P1 --> P2["A table shows every organisation and month<br/>with the amount, plus anyone skipped and why<br/>(e.g. 'missing billing address'). A spreadsheet<br/>of the same thing downloads."]
    P2 --> D
    D -- "Generate &amp; download" --> G1["Each invoice is produced and filed against its<br/>own organisation — exactly as if it had been<br/>generated from that org's page, so it also shows<br/>up in that org's invoice list."]
    G1 --> G2["An organisation missing its billing name or<br/>address is SKIPPED and listed. One incomplete<br/>organisation never stops the rest of the run."]
    G2 --> G3["Everything is bundled into ONE zip: a folder per<br/>organisation, plus a summary spreadsheet. The<br/>download starts on its own."]
    D -- "Export existing" --> E1["Nothing new is created — only invoices that<br/>already exist for those months are collected<br/>into the same kind of zip. A month with no<br/>invoice is listed as 'no invoice'."]
    G3 --> R["Re-running a month that was already invoiced<br/>reuses the SAME invoice number — it never<br/>issues a second number for the same month."]
    E1 --> R${PRODUCT_LEGEND}
    class A,B,C,P1,P2,G1,G3,E1,R both
    style G2 fill:#fff3e0,stroke:#e65100`,
  },
  {
    section: 'Billing & Payments',
    key: 'testing-org-conversion',
    title: 'Testing-org sign-up → their own paid org',
    summary:
      "Broker AI only. New individuals sign up into a couple of SHARED 'testing' orgs and run a FREE per-person trial. They can't pay there (the org is shared with strangers, and payment is always ORG-level — there is no per-person payment). When someone hits their trial limit they ask for a team account; the team one-click creates them their OWN billable org and moves them into it, where they self-serve-pay. Ships behind a master flag — dark until it's switched on.",
    mermaid: `flowchart TD
    A["A new person signs up"] --> B["Lands in a shared 'testing' org<br/>(free PER-PERSON trial limit)"]
    B --> C["Runs tools on their free trial"]
    C --> D{"Hit their personal trial limit?"}
    D -- "No, keep working" --> C
    D -- "Yes" --> E["Shown a 'Set up your team account' form<br/>(firm name + account-manager email)"]
    E --> F["Lands in the admin<br/>Conversion Requests queue"]
    F --> G["Team clicks 'Approve & provision'"]
    G --> H["A fresh billable org is created AND<br/>the person is MOVED out of the testing org into it"]
    H --> I["From their OWN org they choose a plan and pay<br/>(payment is always ORG-level — never per-person)"]
    I --> J["Their new org's shared pool activates"]
    class A,B,C,D,E,F,G,H,I,J broker${PRODUCT_LEGEND}
    style E fill:#fff3e0,stroke:#e65100
    style H fill:#fff3e0,stroke:#e65100`,
  },
  {
    section: 'Billing & Payments',
    key: 'team-members-visibility',
    title: 'Team management — when the team list shows',
    summary:
      "Broker AI. On the Manage Subscription page, the Team Members section — set the org owner and invite teammates for free — appears only when the organisation is on the shared-pool model (everyone draws from ONE org allowance, so extra teammates are free). It shows in EVERY state: on a free trial, mid-signup before a card is added, or fully paid. It is NOT tied to whether they've paid yet. An org where each person buys their own plan (not a pool) never shows it.",
    mermaid: `flowchart TD
    A["A broker opens the Manage Subscription page"] --> B{"Is this org on the shared-pool model?<br/>(everyone draws from ONE org allowance,<br/>and extra teammates are free)"}
    B -- "No (each person buys their own plan)" --> N["No Team Members section.<br/>Each person manages their own plan."]
    B -- "Yes (shared pool)" --> C{"What is their subscription state?"}
    C -- "On a free trial" --> S["Show the Team Members section"]
    C -- "Mid-signup (no card added yet)" --> S
    C -- "Fully paid / active" --> S
    S --> D["They can set the org owner and<br/>invite teammates for free —<br/>in EVERY state, paid or not."]
    class A,B,C,S,D broker${PRODUCT_LEGEND}`,
  },
  {
    section: 'Communications',
    key: 'customer-comms',
    title: 'Customer comms — every email & notification, and when it fires',
    summary:
      "Every automatic message a CUSTOMER gets from the system, grouped by journey stage — being approved/turned on, trial nudges as they near the cap, billing, document delivery, and the email-in tools. So you can see at a glance WHEN comms fire. 📧 = email, 🔔 = in-app notification. (Internal alerts that land on YOUR team's admin dashboard — new signups, trial conversions, run-limit alerts — are NOT shown here; this is only what the customer sees. Account-manager run-limit emails + invoices go to the salesperson, also not shown.)",
    mermaid: `flowchart TB
    subgraph G1["① Signup &amp; onboarding"]
      direction LR
      A1["📧 You're approved —<br/>log in now"]
      A2["📧 You've been<br/>invited to a team"]
      A3["📧 Team-account<br/>request received"]
      A4["📧 Your team<br/>account is ready"]
    end
    subgraph G2["② Trial &amp; usage limits · Broker AI"]
      direction LR
      B1["📧 80% of your<br/>trial used"]
      B2["📧 Trial limit<br/>hit (100%)"]
      B3["📧 Over your<br/>trial limit"]
      B5["📧 Trial ended —<br/>choose a plan"]
    end
    subgraph G3["③ Billing &amp; payments · Broker AI"]
      direction LR
      C1["📧 Card payment failed —<br/>update it (grace, keep working)"]
      C2["📧 Account suspended —<br/>read-only (repeated failures)"]
      C3["📧 Payment recovered —<br/>back to normal"]
    end
    subgraph G4["④ Your documents / jobs"]
      direction LR
      D1["📧 Your document is ready<br/>(if completion emails on)"]
      D2["📧 A problem with your job<br/>(our team notifies you)"]
      D3["📧 Your job issue is<br/>resolved (team follow-up)"]
    end
    subgraph G5["⑤ Emailed-in documents · Intelligent Automation"]
      direction LR
      E1["🔔 We're processing your<br/>emailed document (in-app)"]
      E2["📧 Your document is ready<br/>(reply with attachment)"]
      E3["📧 Couldn't process your email<br/>(bad/missing PDF password, error)"]
    end
    subgraph G6["⑥ Anytime"]
      direction LR
      F1["📧 An announcement / nudge from our team<br/>(e.g. 'pay before month-end' — a broadcast)"]
    end
    A1 ~~~ B1
    B1 ~~~ C1
    C1 ~~~ D1
    D1 ~~~ E1
    E1 ~~~ F1
    class A3,A4,B1,B2,B3,B5,C1,C2,C3 broker
    class E1,E2,E3 iam
    class A1,A2,D1,D2,D3,F1 both${PRODUCT_LEGEND}`,
  },

  // ── Access & Modules — how an admin turns individual tools on/off for one
  //    organisation, and the two-gate model (available for the product, then
  //    ticked on for the org). Plain English only; works for both products.
  {
    section: 'Access & Modules',
    key: 'org-modules',
    title: 'Turning tools on/off for an organisation',
    summary:
      "How an admin controls which tools an organisation can see — turning individual modules on or off for that org, with no code change and no deploy. Works for both products. There are two gates: a tool must first be AVAILABLE for the org's product (decided when the tool is built / released), and then it must be TICKED ON for that specific org on its Features screen. The org's menu — for example what shows under 'Quote Comparison' — is simply whatever is ticked here.",
    mermaid: `flowchart TD
    A["Someone asks: this org should see<br/>tool X, but not tool Y."] --> B["Admin panel → Organisations →<br/>open the org"]
    B --> C["On the org's page, find the<br/>Features section → click Edit"]
    C --> D["A checklist of tools appears,<br/>grouped by category"]
    D --> N{"Which tools are IN the checklist?"}
    N --> N1["Only tools that are AVAILABLE for this<br/>org's product.<br/><b>Gate 1</b> — decided when a tool is built /<br/>released. If a tool isn't listed, it isn't<br/>available for this product yet — that's a<br/>dev / release change, not a tick here."]
    N1 --> E{"For each listed tool:<br/><b>Gate 2</b> — tick to turn ON,<br/>untick to turn OFF (per THIS org)"}
    E -- "Tick" --> F["ON for this org"]
    E -- "Untick" --> G["OFF for this org"]
    F --> H["Click Save"]
    G --> H
    H --> I["Saved to THIS org only. No other org<br/>changes. No deploy needed."]
    I --> J["Next time that org's users load the app,<br/>their menu shows exactly the ticked tools —<br/>e.g. what appears under 'Quote Comparison'."]${PRODUCT_LEGEND}`,
  },
];

/** Sections in display order, derived from the flows. */
export function getFlowSections(): { section: string; flows: SystemFlow[] }[] {
  const order: string[] = [];
  const bySection = new Map<string, SystemFlow[]>();
  for (const flow of SYSTEM_FLOWS) {
    if (!bySection.has(flow.section)) {
      bySection.set(flow.section, []);
      order.push(flow.section);
    }
    bySection.get(flow.section)!.push(flow);
  }
  return order.map((section) => ({ section, flows: bySection.get(section)! }));
}

/** Look up a flow by key (the URL/active-topic id). */
export function getFlowByKey(key: string): SystemFlow | undefined {
  return SYSTEM_FLOWS.find((f) => f.key === key);
}
